import { firebaseAuth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ── Auth kick-out on expired / revoked session ───────

function handleAuthFailure() {
  signOut(firebaseAuth).catch(() => {});
  window.location.replace('/login');
}

async function getToken(): Promise<string> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  public?: boolean;
}

async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { public: isPublic, headers: extraHeaders, body, ...rest } = options;
  const authHeader = isPublic ? {} : { Authorization: `Bearer ${await getToken()}` };
  const contentHeader = body !== undefined ? { 'Content-Type': 'application/json' } : {};

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    body,
    headers: { ...contentHeader, ...authHeader, ...extraHeaders } as HeadersInit,
  });

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) { handleAuthFailure(); }
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, error.message ?? res.statusText, error);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Partner endpoints ─────────────────────────────────

export interface PartnerInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface PartnerMeResponse {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'OPERATOR';
  partner: PartnerInfo;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  plan: 'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  allowedDomain: string | null;
  autoProvision: boolean;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isSupportAccount: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface TenantDetail extends Tenant {
  users: TenantUser[];
  customDomain: string | null;
}

export interface ImpersonationToken {
  token: string;
  expiresAt: string;
  supportAccountEmail: string;
  note: string;
}

export const partnerApi = {
  me: () => apiFetch<PartnerMeResponse>('/v1/partner/me'),

  tenants: {
    list: (params?: { page?: number; limit?: number; status?: string }) => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.status) qs.set('status', params.status);
      const q = qs.toString();
      return apiFetch<{ data: Tenant[]; total: number; hasMore: boolean }>(
        `/v1/partner/tenants${q ? `?${q}` : ''}`,
      );
    },

    get: (tenantId: string) =>
      apiFetch<TenantDetail>(`/v1/partner/tenants/${tenantId}`),

    create: (payload: { name: string; slug: string; plan?: string; allowedDomain?: string; autoProvision?: boolean }) =>
      apiFetch<Tenant>('/v1/partner/tenants', { method: 'POST', body: JSON.stringify(payload) }),

    update: (tenantId: string, payload: Partial<{ name: string; status: string; plan: string; allowedDomain: string; autoProvision: boolean }>) =>
      apiFetch<Tenant>(`/v1/partner/tenants/${tenantId}`, { method: 'PATCH', body: JSON.stringify(payload) }),

    impersonate: (tenantId: string) =>
      apiFetch<ImpersonationToken>(`/v1/partner/tenants/${tenantId}/impersonate`, { method: 'POST' }),

    inviteUser: (tenantId: string, payload: { email: string; name?: string; role?: string }) =>
      apiFetch<TenantUser>(`/v1/partner/tenants/${tenantId}/users`, { method: 'POST', body: JSON.stringify(payload) }),

    updateUserRole: (tenantId: string, userId: string, role: string) =>
      apiFetch<TenantUser>(`/v1/partner/tenants/${tenantId}/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),

    removeUser: (tenantId: string, userId: string) =>
      apiFetch<{ removed: boolean; email: string }>(`/v1/partner/tenants/${tenantId}/users/${userId}`, { method: 'DELETE' }),

    seed: (tenantId: string) =>
      apiFetch<{ tenantId: string; tenantName: string; seeded: boolean; created: Record<string, number> }>(`/v1/partner/tenants/${tenantId}/seed`, { method: 'POST' }),
  },
};
